/**
 * MobileBottomNav - NBA Top Shot-inspired bottom navigation bar
 * Premium, sleek design for mobile/tablet views
 * Shows only on screens < 768px (md breakpoint)
 * Features haptic feedback on tap for native app feel
 */
import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeftRight, 
  Zap,
  Trophy,
  Gem,
  ShieldCheck,
  Radio,
  DollarSign,
  ScanLine
} from "lucide-react";
import { haptic } from "../../utils/nativeBridge";
import { API } from "../../utils";

const MobileBottomNav = ({ user, token }) => {
  const location = useLocation();
  // Red dot on Locker tab — mirrors the desktop Navbar treatment. Lights up
  // when the user has shipped hobby cards they haven't seen yet (compares
  // collectible.hobby_shipped_at vs users.last_seen_locker_at). Clears when
  // the user opens /locker (LockerPage fires POST /api/locker/mark-seen).
  const [unseenLockerCount, setUnseenLockerCount] = useState(0);

  useEffect(() => {
    if (!user || !token) { setUnseenLockerCount(0); return; }
    let cancelled = false;
    const fetchUnseen = async () => {
      try {
        const res = await axios.get(`${API}/locker/unseen-shipping-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled) setUnseenLockerCount(res.data?.count || 0);
      } catch (err) {
        // Silent — non-critical endpoint, never spam the mobile console
      }
    };
    fetchUnseen();
    const id = setInterval(fetchUnseen, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, token, location.pathname]);
  
  // Live break red-dot — pulses when ANY break is currently `live`.
  // Calls /api/live-breaks?status=live every 60s. Cheap query — single
  // indexed lookup on the drops collection.
  const [liveBreakCount, setLiveBreakCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const res = await axios.get(`${API}/live-breaks/?status=live`);
        if (!cancelled) setLiveBreakCount(Array.isArray(res.data) ? res.data.length : 0);
      } catch (err) {
        // silent — non-critical
      }
    };
    fetchLive();
    const id = setInterval(fetchLive, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [location.pathname]);

  // Navigation items: EMERGE (public ledger) replaces Activity Feed as the
  // primary Explore shortcut across the site.
  // NOTE: Added "Sell" (Buyback Program) as a new shortcut WITHOUT removing
  // any existing icons. The bar now shows 6 items on mobile — Tailwind's
  // built-in flex distribution keeps them evenly spaced down to iPhone SE.
  //
  // 2026-06 rename: "Drops" tab now routes to `/drops` (formerly `/play`)
  // and "Play" tab routes to `/play` (formerly `/challenges`). The URLs
  // now match the visible labels. See the path swap in App.js routes.
  const navItems = [
    {
      path: "/drops",
      label: "Drops",
      icon: Zap
    },
    { 
      path: "/live", 
      label: "Live", 
      icon: Radio
    },
    { 
      path: "/marketplace", 
      label: "Swap", 
      icon: ArrowLeftRight
    },
    { 
      path: "/sell", 
      label: "Sell", 
      icon: DollarSign
    },
    {
      // Scan AI — Monster Meter card identifier. Added 2026-06-04 per
      // user directive WITHOUT removing any existing icons. The bar now
      // shows 7 items on mobile, still within Tailwind's md breakpoint.
      path: "/scan",
      label: "Scan AI",
      icon: ScanLine,
      isNew: true,
    },
    {
      path: "/play",
      label: "Play",
      icon: Trophy
    },
    { 
      path: user ? "/locker" : "/login", 
      label: "Locker", 
      icon: Gem
    }
  ];

  const isActive = (path, matchPrefix = false) => {
    if (matchPrefix) {
      return location.pathname.startsWith(path);
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  // Handle nav item tap with haptic feedback
  const handleNavTap = (path, matchPrefix) => {
    const active = isActive(path, matchPrefix);
    if (!active) {
      haptic.medium();
    }
  };

  return (
    <>
      {/* Spacer to prevent content from being hidden behind the fixed nav */}
      <div className="md:hidden h-20" />
      
      {/* Bottom Navigation Bar */}
      <nav 
        className="md:hidden fixed bottom-0 left-0 right-0 z-40"
        data-testid="mobile-bottom-nav"
      >
        {/* Dark background with subtle top border - NBA Top Shot style */}
        <div className="bg-[#0d0d0f] border-t border-gray-800/60">
          {/* Navigation items container */}
          <div className="flex items-stretch justify-around max-w-lg mx-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.matchPrefix);
              
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => handleNavTap(item.path, item.matchPrefix)}
                  className="flex-1 flex flex-col items-center justify-center py-3 px-1 transition-all duration-150 active:scale-95"
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  {/* Icon container */}
                  <div className={`relative mb-1 transition-all duration-150 ${
                    active ? "transform scale-105" : ""
                  }`}>
                    <Icon 
                      size={24} 
                      strokeWidth={active ? 2 : 1.5}
                      className={`transition-colors duration-150 ${
                        active 
                          ? "text-white" 
                          : "text-gray-500"
                      }`}
                    />
                    {/* Locker tab red dot — mobile mirror of the desktop
                        version. Same threshold + clear-on-visit behaviour. */}
                    {item.label === "Locker" && unseenLockerCount > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 flex items-center justify-center"
                        data-testid="locker-unseen-dot-mobile"
                        aria-label={`${unseenLockerCount} unseen shipping update${unseenLockerCount === 1 ? "" : "s"}`}
                      >
                        <span className="absolute inline-flex h-3 w-3 rounded-full bg-orange-500 opacity-60 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 brand-gradient-bg shadow-[0_0_6px_rgba(249,115,22,0.8)]" />
                      </span>
                    )}
                    {/* Live Breaks tab red dot — pulses red whenever a host
                        is currently streaming. */}
                    {item.label === "Live" && liveBreakCount > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 flex items-center justify-center"
                        data-testid="live-pulse-dot-mobile"
                        aria-label={`${liveBreakCount} live break${liveBreakCount === 1 ? "" : "s"} now`}
                      >
                        <span className="absolute inline-flex h-3 w-3 rounded-full bg-red-500 opacity-70 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.9)]" />
                      </span>
                    )}
                  </div>
                  
                  {/* Label */}
                  <span className={`text-[11px] font-medium transition-colors duration-150 ${
                    active 
                      ? "text-white" 
                      : "text-gray-500"
                  }`}>
                    {item.label}
                  </span>
                </NavLink>
              );
            })}
          </div>
          
          {/* Safe area padding for iOS home indicator */}
          <div className="h-safe-area-bottom bg-[#0d0d0f]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} />
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
